// Advanced Slider Logic (Infinite Seamless Loop, Touch/Drag)
class CustomSlider extends HTMLElement {
  constructor() {
    super();
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = 0;
  }

  connectedCallback() {
    // When Shopify Theme Editor injects HTML, we need to wait a tick for children to parse
    setTimeout(() => {
      this.track = this.querySelector('[data-slider]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dotsContainer = this.querySelector('[data-dots]');
      
      if (!this.track || this.slides.length === 0) return;
      
      this.totalSlides = this.slides.length;
      this.currentSlide = this.totalSlides; // start at first real slide because we will prepend a full set of clones
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = parseInt(this.dataset.interval) || 5000;
      
      this.init();
    }, 0);
  }

  init() {
    if (this.totalSlides <= 1) return;
    
    this.setupClones();
    this.createDots();
    this.bindEvents();
    
    // Initial positioning without transition
    this.setTransition(0);
    this.updatePosition();
    
    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  setupClones() {
    function makeInert(el) {
      el.dataset.clone = "true";
      el.removeAttribute('data-slide');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('inert', '');
      el.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(focusable => {
        focusable.setAttribute('tabindex', '-1');
        focusable.setAttribute('aria-hidden', 'true');
      });
    }

    // Clone all slides and append them to the end
    this.slides.forEach(slide => {
      const clone = slide.cloneNode(true);
      makeInert(clone);
      this.track.appendChild(clone);
    });
    
    // Clone all slides and prepend them to the beginning
    for (let i = this.totalSlides - 1; i >= 0; i--) {
      const clone = this.slides[i].cloneNode(true);
      makeInert(clone);
      this.track.insertBefore(clone, this.track.firstChild);
    }
    
    this.allItems = Array.from(this.track.children);
  }

  bindEvents() {
    // Touch/Mouse events
    this.track.addEventListener('pointerdown', this.touchStart.bind(this));
    window.addEventListener('pointerup', this.touchEnd.bind(this));
    window.addEventListener('pointermove', this.touchMove.bind(this));
    
    // Prevent default drag behaviors on images so we can swipe them
    this.track.querySelectorAll('img').forEach(img => {
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });
    
    // Transition events for seamless loop
    this.track.addEventListener('transitionend', this.handleTransitionEnd.bind(this));
    
    // Autoplay pause/resume (only for mouse to prevent mobile touch tap from pausing permanently)
    this.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') this.stopAutoplay();
    });
    this.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse' && this.autoplay) this.startAutoplay();
    });
    
    window.addEventListener('pointercancel', this.touchEnd.bind(this));
    
    // Handle resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      this.setTransition(0);
      this.updatePosition();
      resizeTimer = setTimeout(() => {
        this.updatePosition();
      }, 250);
    });

    // Pause autoplay when browser tab is hidden to prevent background rendering bugs
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        if (this.autoplay) this.startAutoplay();
      }
    });
  }

  createDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    for (let i = 0; i < this.totalSlides; i++) {
      const dot = document.createElement('button');
      dot.classList.add('slider-dot');
      if (i === 0) dot.classList.add('is-active');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => {
        this.goToSlide(i + this.totalSlides);
        if (this.autoplay) this.startAutoplay(); // Reset timer on click
      });
      this.dotsContainer.appendChild(dot);
    }
  }

  updateDots() {
    if (!this.dotsContainer) return;
    const dots = this.dotsContainer.children;
    
    let realIndex = (this.currentSlide - this.totalSlides) % this.totalSlides;
    if (realIndex < 0) realIndex += this.totalSlides;
    
    Array.from(dots).forEach((dot, i) => {
      dot.classList.toggle('is-active', i === realIndex);
    });
  }

  touchStart(event) {
    if (this.totalSlides <= 1) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return; // Only left click
    
    this.isDragging = true;
    this.startPos = this.getPositionX(event);
    this.setTransition(0); // Remove transition during drag
    this.animationID = requestAnimationFrame(this.animationLoop.bind(this));
    this.track.classList.add('is-grabbing');
    this.stopAutoplay();
  }

  touchMove(event) {
    if (!this.isDragging) return;
    const currentPosition = this.getPositionX(event);
    const diff = currentPosition - this.startPos;
    this.currentTranslate = this.prevTranslate + diff;
  }

  touchEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    cancelAnimationFrame(this.animationID);
    this.track.classList.remove('is-grabbing');
    
    const movedBy = this.currentTranslate - this.prevTranslate;
    
    const slideWidth = this.slides[0].offsetWidth;
    const style = window.getComputedStyle(this.track);
    const gap = parseFloat(style.gap) || 0;
    const moveAmount = slideWidth + gap;
    const threshold = moveAmount / 4;
    
    if (movedBy < -threshold) this.currentSlide += 1;
    if (movedBy > threshold) this.currentSlide -= 1;
    
    this.goToSlide(this.currentSlide);
    
    if (this.autoplay) this.startAutoplay();
  }

  getPositionX(event) {
    return event.clientX;
  }

  animationLoop() {
    if (this.isDragging) {
      this.setTranslate(this.currentTranslate);
      requestAnimationFrame(this.animationLoop.bind(this));
    }
  }

  setTranslate(pos) {
    this.track.style.transform = `translate3d(${pos}px, 0, 0)`;
  }

  setTransition(duration) {
    this.track.style.transition = `transform ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
  }

  goToSlide(index) {
    this.currentSlide = index;
    this.setTransition(600); // 600ms smooth transition
    this.updatePosition();
    this.updateDots();
    
    // Bulletproof fallback: If the browser is in the background or in an iframe, 
    // it may suppress the 'transitionend' event entirely. We force it to run here.
    clearTimeout(this.transitionFallback);
    this.transitionFallback = setTimeout(() => {
      this.handleTransitionEnd();
    }, 650); // Slightly longer than the CSS transition duration
  }

  updatePosition() {
    const slideWidth = this.slides[0].offsetWidth;
    const style = window.getComputedStyle(this.track);
    const gap = parseFloat(style.gap) || 0;
    const moveAmount = slideWidth + gap;
    
    let offset = 0;
    if (this.dataset.centered === 'true') {
      const containerWidth = this.offsetWidth;
      offset = (containerWidth - slideWidth) / 2;
    }
    
    this.currentTranslate = -(this.currentSlide * moveAmount) + offset;
    this.prevTranslate = this.currentTranslate;
    this.setTranslate(this.currentTranslate);
  }

  handleTransitionEnd() {
    // If we scrolled past the real slides into the end clones
    if (this.currentSlide >= this.totalSlides * 2) {
      this.setTransition(0);
      this.currentSlide = this.currentSlide - this.totalSlides;
      this.updatePosition();
    }
    // If we scrolled backwards into the start clones
    if (this.currentSlide < this.totalSlides) {
      this.setTransition(0);
      this.currentSlide = this.currentSlide + this.totalSlides;
      this.updatePosition();
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    this.timer = setInterval(() => {
      this.goToSlide(this.currentSlide + 1);
    }, this.interval);
  }

  stopAutoplay() {
    clearInterval(this.timer);
  }
}

customElements.define('custom-slider', CustomSlider);
